import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  fetchSkillsBatches,
  fetchSkillsBatchQueue,
  fetchSessionTranscript,
  fetchProductTags,
  fetchSessionRuns,
  fetchVocabulary,
  fetchSessionReviews,
  saveQuoteReview,
  fetchRunResults,
  fetchChallengeQuestion,
  saveLlmReview,
  isEndpointMissing,
} from "./skillsProfileApi";
import { buildPreviewChallenge } from "utils/challenge";
import { ConsensusActivityRender } from "../../Session/Activity/activities/ConsensusActivity/ConsensusActivityRender";
import { CopyIconButton, StandardsBadges } from "../EvaluatorReview/displayPrimitives";
import "../EvaluatorReview/evaluatorPreview.css";
import "./skillsProfile.css";

const parseType = (type) => {
  switch (type) {
    case "bot_message":
      return "OKO";
    case "speech":
      return "Speaker";
    case "event":
      return "[Event]";
    case "status":
    case "system":
      return "[System]";
    default:
      return type || "Speaker";
  }
};

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${remainingSeconds}s`;
};

const formatRunDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
};

const cleanEventContent = (content) => {
  if (!content) return "";
  const challengeResponseIndex = content.indexOf("challenge_response:");
  if (challengeResponseIndex !== -1) {
    try {
      const jsonStr = content.substring(challengeResponseIndex + "challenge_response:".length).trim();
      const response = JSON.parse(jsonStr);
      if (response.solution !== undefined) {
        return `Submitted answer: ${response.solution}`;
      }
      if (response.option_index !== undefined) {
        return `Selected option: ${response.option_index}`;
      }
    } catch {
      // Return raw content if parsing fails
    }
  }
  return content;
};

const isQuotableSpeaker = (event) => {
  if (!event) return false;
  const content = cleanEventContent(event.content);
  if (typeof content === "string") {
    return content.trim().length > 0;
  }
  return Boolean(content);
};

const getSpeakerClass = (speaker) => {
  const s = (speaker || "").toLowerCase();
  if (s.includes("oko") || s.includes("tutor")) return "spk-oko";
  if (s.includes("student a") || s.endsWith(" a")) return "spk-student-a";
  if (s.includes("student b") || s.endsWith(" b")) return "spk-student-b";
  if (s.includes("student c") || s.endsWith(" c")) return "spk-student-c";
  if (s.includes("student d") || s.endsWith(" d")) return "spk-student-d";
  return "spk-student";
};

const getPhases = (data) => {
  if (!data) return [];
  const root = data?.session || data?.transcript?.session || data?.transcript || data;
  if (Array.isArray(root?.phases)) return root.phases;
  if (Array.isArray(root)) return root;
  return [];
};

export const SkillsProfileReview = () => {
  const history = useHistory();
  const location = useLocation();

  // Read query params on mount (Scope 1.1)
  const initialUrlBatchRef = useRef(new URLSearchParams(location.search).get("batch"));
  const initialUrlSessionRef = useRef(new URLSearchParams(location.search).get("session"));
  const initialUrlLlmRef = useRef(new URLSearchParams(location.search).get("llm"));

  // Batches and Queue
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [queue, setQueue] = useState(null);
  const [currentQueueIndex, setCurrentQueueIndex] = useState(0);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [queueLoading, setQueueLoading] = useState(false);

  // Session Data
  const [sessionLoading, setSessionLoading] = useState(false);
  const [transcriptData, setTranscriptData] = useState(null);
  const [vocabulary, setVocabulary] = useState({ skills: ["Collaboration"], tags: [] });
  const [sessionRuns, setSessionRuns] = useState([]);
  const [productTags, setProductTags] = useState([]);

  // LLM Tags Multi-select sources (Scope 1.5)
  const [selectedLlmSources, setSelectedLlmSources] = useState(() =>
    initialUrlLlmRef.current ? initialUrlLlmRef.current.split(",").filter(Boolean) : []
  );
  const [llmResults, setLlmResults] = useState([]);

  // Challenge Question Detail Cache (Scope 1.3)
  const [questionsById, setQuestionsById] = useState({});

  // Reviews State
  // smeReviews: { [quoteKey]: { [skill]: { tags: Set, reasons: { [tag]: text }, submitted: bool, skill: string } } }
  const [smeReviews, setSmeReviews] = useState({});
  // llmReviews: { [resultId]: { agree: string, confidence: string, reason: string, submitted: bool } }
  const [llmReviews, setLlmReviews] = useState({});

  // Active selection: { kind: 'quote' | 'llm', data: ... }
  const [selection, setSelection] = useState(null);

  // Quote Annotation Form State (when selection.kind === 'quote')
  const [activeSkill, setActiveSkill] = useState("Collaboration");
  const [activeQuoteTags, setActiveQuoteTags] = useState(new Set());
  const [activeQuoteReasons, setActiveQuoteReasons] = useState({});
  const [quoteSaving, setQuoteSaving] = useState(false);
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);

  // LLM Review Form State (when selection.kind === 'llm')
  const [activeLlmAgree, setActiveLlmAgree] = useState(null);
  const [activeLlmConfidence, setActiveLlmConfidence] = useState(null);
  const [activeLlmReason, setActiveLlmReason] = useState("");
  const [llmSaving, setLlmSaving] = useState(false);
  const [llmSubmitted, setLlmSubmitted] = useState(false);

  // Error messages
  const [pageError, setPageError] = useState(null);

  // 1. Initial Load: Batches & Vocabulary
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      setBatchesLoading(true);
      setPageError(null);
      try {
        const [batchesRes, vocabRes] = await Promise.all([
          fetchSkillsBatches(),
          fetchVocabulary(),
        ]);
        if (!mounted) return;
        const batchList = Array.isArray(batchesRes) ? batchesRes : batchesRes?.batches || [];
        setBatches(batchList);
        if (vocabRes?.skills?.length || vocabRes?.tags?.length) {
          setVocabulary({
            skills: vocabRes.skills?.length ? vocabRes.skills : ["Collaboration"],
            tags: vocabRes.tags || [],
          });
          if (vocabRes.skills?.[0]) setActiveSkill(vocabRes.skills[0]);
        }
        if (batchList.length > 0) {
          const match = initialUrlBatchRef.current && batchList.find((b) => b.id === initialUrlBatchRef.current);
          if (match) {
            setSelectedBatchId(match.id);
          } else {
            setSelectedBatchId(batchList[0].id);
          }
        }
      } catch (err) {
        if (!mounted) return;
        if (isEndpointMissing(err)) {
          setPageError("Skills Profile backend endpoint not available.");
        } else {
          setPageError("Failed to load review batches.");
        }
      } finally {
        if (mounted) setBatchesLoading(false);
      }
    };
    init();
    return () => {
      mounted = false;
    };
  }, []);

  // 2. Load Queue when Selected Batch Changes
  useEffect(() => {
    if (!selectedBatchId) {
      setQueue(null);
      return;
    }
    let mounted = true;
    const loadQueue = async () => {
      setQueueLoading(true);
      setPageError(null);
      try {
        const queueRes = await fetchSkillsBatchQueue(selectedBatchId);
        if (!mounted) return;
        setQueue(queueRes);
        const isUrlBatchMatch = !initialUrlBatchRef.current || selectedBatchId === initialUrlBatchRef.current;
        if (initialUrlSessionRef.current && queueRes.items?.length > 0 && isUrlBatchMatch) {
          const targetIdx = queueRes.items.findIndex(
            (it) => it.session_id === initialUrlSessionRef.current
          );
          if (targetIdx !== -1) {
            setCurrentQueueIndex(targetIdx);
          } else {
            // Unknown session falls back to first queue item (Scope 1.1)
            setCurrentQueueIndex(0);
          }
          initialUrlSessionRef.current = null;
        } else {
          setCurrentQueueIndex(0);
        }
      } catch (err) {
        if (!mounted) return;
        if (isEndpointMissing(err)) {
          setPageError("Skills Profile backend endpoint not available.");
        } else {
          setPageError("Failed to load batch queue.");
        }
      } finally {
        if (mounted) setQueueLoading(false);
      }
    };
    loadQueue();
    return () => {
      mounted = false;
    };
  }, [selectedBatchId]);

  // Current session item
  const currentQueueItem = queue?.items?.[currentQueueIndex] || null;
  const currentSessionId = currentQueueItem?.session_id || null;

  const currentSessionIdRef = useRef(currentSessionId);
  currentSessionIdRef.current = currentSessionId;

  // Sync batch, session, and LLM query params via history.replace (Scope 1.1)
  useEffect(() => {
    if (!selectedBatchId) return;
    const params = new URLSearchParams(location.search);
    let changed = false;

    if (params.get("batch") !== selectedBatchId) {
      params.set("batch", selectedBatchId);
      changed = true;
    }

    if (currentSessionId) {
      if (params.get("session") !== currentSessionId) {
        params.set("session", currentSessionId);
        changed = true;
      }
    } else if (params.has("session")) {
      params.delete("session");
      changed = true;
    }

    const llmParam = selectedLlmSources.join(",");
    if (llmParam) {
      if (params.get("llm") !== llmParam) {
        params.set("llm", llmParam);
        changed = true;
      }
    } else if (params.has("llm")) {
      params.delete("llm");
      changed = true;
    }

    if (changed) {
      const newSearch = params.toString() ? `?${params.toString()}` : "";
      if (newSearch !== location.search) {
        history.replace({
          pathname: location.pathname,
          search: newSearch,
        });
      }
    }
  }, [selectedBatchId, currentSessionId, selectedLlmSources, location.pathname, location.search, history]);

  // 3. Load Session Data when Current Session Changes
  useEffect(() => {
    if (!currentSessionId) {
      setTranscriptData(null);
      setSessionRuns([]);
      setProductTags([]);
      setLlmResults([]);
      setSmeReviews({});
      setLlmReviews({});
      setSelection(null);
      return;
    }

    let mounted = true;
    const loadSession = async () => {
      setSessionLoading(true);
      setSelection(null);
      setPageError(null);
      try {
        const [transcriptRes, runsRes, reviewsRes] = await Promise.all([
          fetchSessionTranscript(currentSessionId),
          fetchSessionRuns(currentSessionId),
          fetchSessionReviews(currentSessionId),
        ]);

        if (!mounted) return;
        setTranscriptData(transcriptRes);
        const runsList = Array.isArray(runsRes) ? runsRes : runsRes?.runs || [];
        setSessionRuns(runsList);

        // Hydrate existing human SME reviews for round-trip draft support
        const reviewsMap = {};
        const reviewsList = Array.isArray(reviewsRes) ? reviewsRes : reviewsRes?.reviews || [];
        reviewsList.forEach((rev) => {
          const key = `${rev.quote_timestamp}|${rev.quote}`;
          const skill = rev.skill || "Collaboration";
          reviewsMap[key] = {
            ...reviewsMap[key],
            [skill]: {
              id: rev.id,
              skill,
              tags: new Set(rev.tags || []),
              reasons: rev.reasons || {},
              submitted: (rev.tags && rev.tags.length > 0) || false,
            },
          };
        });
        setSmeReviews(reviewsMap);
      } catch (err) {
        if (!mounted) return;
        if (isEndpointMissing(err)) {
          setPageError("Skills Profile backend endpoint not available.");
        } else {
          setPageError("Failed to load session details.");
        }
      } finally {
        if (mounted) setSessionLoading(false);
      }
    };

    loadSession();
    return () => {
      mounted = false;
    };
  }, [currentSessionId]);

  // 4. Handle LLM Multi-Select Overlay (Scope 1.5)
  useEffect(() => {
    if (!currentSessionId) return;

    let mounted = true;
    const updateLlmOverlay = async () => {
      if (!selectedLlmSources || selectedLlmSources.length === 0) {
        if (mounted) {
          setProductTags([]);
          setLlmResults([]);
        }
        return;
      }

      const hasProduct = selectedLlmSources.includes("product");
      const runIds = selectedLlmSources.filter((s) => s !== "product");

      // 1. Fetch product tags if selected
      if (hasProduct) {
        try {
          const res = await fetchProductTags(currentSessionId);
          const tagsList = Array.isArray(res) ? res : res?.tags || [];
          if (mounted) setProductTags(tagsList);
        } catch (err) {
          if (mounted) {
            setProductTags([]);
            if (isEndpointMissing(err)) {
              setPageError("Skills Profile backend endpoint not available.");
            } else {
              setPageError("Failed to load product tags.");
            }
          }
        }
      } else {
        if (mounted) setProductTags([]);
      }

      // 2. Fetch run results for each selected run
      if (runIds.length === 0) {
        if (mounted) setLlmResults([]);
      } else {
        const runPromises = runIds.map(async (rid) => {
          const res = await fetchRunResults(rid, currentSessionId);
          const rawResults = Array.isArray(res) ? res : res?.results || [];
          return rawResults.map((r) => ({ ...r, run_id: rid }));
        });
        const settlementResults = await Promise.allSettled(runPromises);
        if (!mounted) return;

        const successfulResults = [];
        let failedCount = 0;
        let endpointMissing = false;

        settlementResults.forEach((settled) => {
          if (settled.status === "fulfilled") {
            successfulResults.push(settled.value);
          } else {
            failedCount += 1;
            if (isEndpointMissing(settled.reason)) {
              endpointMissing = true;
            }
          }
        });

        const mergedResults = successfulResults.flat();
        if (successfulResults.length === 0 && failedCount > 0) {
          setLlmResults([]);
        } else {
          setLlmResults(mergedResults);
        }

        // Prepopulate llmReviews from existing_review on the results for round-trip draft support
        setLlmReviews((prev) => {
          const next = { ...prev };
          mergedResults.forEach((r) => {
            if (r.existing_review) {
              next[r.id] = {
                agree: r.existing_review.agreement || null,
                confidence: r.existing_review.confidence || null,
                reason: r.existing_review.reason || "",
                submitted: Boolean(r.existing_review.agreement),
              };
            }
          });
          return next;
        });

        if (failedCount > 0) {
          if (endpointMissing) {
            setPageError("Skills Profile backend endpoint not available.");
          } else {
            setPageError(
              `Failed to load ${failedCount} run${failedCount > 1 ? "s" : ""}.`
            );
          }
        }
      }
    };

    updateLlmOverlay();
    return () => {
      mounted = false;
    };
  }, [selectedLlmSources, currentSessionId]);

  // Handle Quote Selection
  const handleSelectQuote = useCallback(
    (event) => {
      const quoteText = cleanEventContent(event.content);
      const quoteKey = `${event.timestamp}|${quoteText}`;
      const quoteReviews = smeReviews[quoteKey] || {};
      const skillForQuote = quoteReviews[activeSkill]
        ? activeSkill
        : Object.keys(quoteReviews)[0] || activeSkill;
      const existing = quoteReviews[skillForQuote];

      setSelection({
        kind: "quote",
        key: quoteKey,
        event: {
          ...event,
          content: quoteText,
        },
      });

      if (existing) {
        setActiveSkill(skillForQuote);
        setActiveQuoteTags(new Set(existing.tags));
        setActiveQuoteReasons({ ...existing.reasons });
        setQuoteSubmitted(Boolean(existing.submitted));
      } else {
        setActiveQuoteTags(new Set());
        setActiveQuoteReasons({});
        setQuoteSubmitted(false);
      }
    },
    [smeReviews, activeSkill]
  );

  const handleChangeSkill = (skill) => {
    setActiveSkill(skill);
    if (selection?.kind !== "quote") return;
    const existing = smeReviews[selection.key]?.[skill];
    setActiveQuoteTags(new Set(existing?.tags || []));
    setActiveQuoteReasons({ ...(existing?.reasons || {}) });
    setQuoteSubmitted(Boolean(existing?.submitted));
  };

  const handleEditSkillTags = (skill) => {
    handleChangeSkill(skill);
    setQuoteSubmitted(false);
  };

  const handleAddSkill = () => {
    if (!addableSkill) return;
    handleChangeSkill(addableSkill);
    setQuoteSubmitted(false);
  };

  const handleClearSkillTags = async (skill) => {
    if (!selection || selection.kind !== "quote" || !currentSessionId) return;
    const sessionIdAtClear = currentSessionId;
    const quoteKey = selection.key;
    setQuoteSaving(true);
    try {
      await saveQuoteReview({
        sessionId: sessionIdAtClear,
        quoteTimestamp: selection.event.timestamp,
        quote: selection.event.content,
        skill,
        tags: null,
        reasons: null,
      });

      if (currentSessionIdRef.current === sessionIdAtClear) {
        const remaining = { ...smeReviews[quoteKey] };
        delete remaining[skill];
        setSmeReviews((prev) => {
          const next = { ...prev, [quoteKey]: { ...prev[quoteKey] } };
          delete next[quoteKey][skill];
          return next;
        });
        const hasRemaining = Object.values(remaining).some((r) => r.submitted && r.tags.size > 0);
        if (!hasRemaining) {
          setActiveQuoteTags(new Set());
          setActiveQuoteReasons({});
          setQuoteSubmitted(false);
        }
      }
    } catch (err) {
      if (currentSessionIdRef.current === sessionIdAtClear) {
        if (isEndpointMissing(err)) {
          setPageError("Skills Profile backend endpoint not available.");
        } else {
          setPageError("Failed to clear tags.");
        }
      }
    } finally {
      setQuoteSaving(false);
    }
  };

  // Handle LLM Result Selection
  const handleSelectLlmResult = useCallback(
    (result, event) => {
      const existing = llmReviews[result.id] || (result.existing_review ? {
        agree: result.existing_review.agreement || null,
        confidence: result.existing_review.confidence || null,
        reason: result.existing_review.reason || "",
        submitted: Boolean(result.existing_review.agreement),
      } : null);

      setSelection({
        kind: "llm",
        result,
        event: {
          ...event,
          content: cleanEventContent(event.content),
        },
      });

      if (existing) {
        setActiveLlmAgree(existing.agree);
        setActiveLlmConfidence(existing.confidence);
        setActiveLlmReason(existing.reason || "");
        setLlmSubmitted(Boolean(existing.submitted));
      } else {
        setActiveLlmAgree(null);
        setActiveLlmConfidence(null);
        setActiveLlmReason("");
        setLlmSubmitted(false);
      }
    },
    [llmReviews]
  );

  // Submit Quote Review
  const handleSubmitQuoteReview = async () => {
    if (!selection || selection.kind !== "quote" || !currentSessionId) return;
    const sessionIdAtSubmit = currentSessionId;
    setQuoteSaving(true);
    try {
      const payload = {
        sessionId: sessionIdAtSubmit,
        quoteTimestamp: selection.event.timestamp,
        quote: selection.event.content,
        skill: activeSkill,
        tags: Array.from(activeQuoteTags),
        reasons: activeQuoteReasons,
      };
      await saveQuoteReview(payload);

      if (currentSessionIdRef.current === sessionIdAtSubmit) {
        setSmeReviews((prev) => ({
          ...prev,
          [selection.key]: {
            ...prev[selection.key],
            [activeSkill]: {
              skill: activeSkill,
              tags: new Set(activeQuoteTags),
              reasons: { ...activeQuoteReasons },
              submitted: true,
            },
          },
        }));
        setQuoteSubmitted(true);
      }
    } catch (err) {
      if (currentSessionIdRef.current === sessionIdAtSubmit) {
        if (isEndpointMissing(err)) {
          setPageError("Skills Profile backend endpoint not available.");
        } else {
          setPageError("Failed to save quote review.");
        }
      }
    } finally {
      setQuoteSaving(false);
    }
  };

  // Clear Quote Review Form
  const handleClearQuoteReview = () => {
    setActiveQuoteTags(new Set());
    setActiveQuoteReasons({});
    setQuoteSubmitted(false);
  };

  // Submit LLM Review
  const handleSubmitLlmReview = async () => {
    if (!selection || selection.kind !== "llm") return;
    const sessionIdAtSubmit = currentSessionId;
    setLlmSaving(true);
    try {
      const payload = {
        resultId: selection.result.id,
        agreement: activeLlmAgree,
        confidence: activeLlmConfidence,
        reason: activeLlmReason,
      };
      await saveLlmReview(payload);

      if (currentSessionIdRef.current === sessionIdAtSubmit) {
        setLlmReviews((prev) => ({
          ...prev,
          [selection.result.id]: {
            agree: activeLlmAgree,
            confidence: activeLlmConfidence,
            reason: activeLlmReason,
            submitted: true,
          },
        }));
        setLlmSubmitted(true);
      }
    } catch (err) {
      if (currentSessionIdRef.current === sessionIdAtSubmit) {
        if (isEndpointMissing(err)) {
          setPageError("Skills Profile backend endpoint not available.");
        } else {
          setPageError("Failed to save LLM review.");
        }
      }
    } finally {
      setLlmSaving(false);
    }
  };

  // Clear LLM Review Form
  const handleClearLlmReview = () => {
    setActiveLlmAgree(null);
    setActiveLlmConfidence(null);
    setActiveLlmReason("");
    setLlmSubmitted(false);
  };

  // Active skill tags from vocabulary
  const activeSkillTags = useMemo(() => {
    if (!vocabulary?.tags) return [];
    return vocabulary.tags.filter((t) => !t.skill || t.skill === activeSkill);
  }, [vocabulary, activeSkill]);

  // Index product tags by quote key: timestamp|text
  const productTagsByKey = useMemo(() => {
    const map = {};
    productTags.forEach((pt) => {
      const key = `${pt.quote_timestamp}|${pt.quote}`;
      if (!map[key]) map[key] = [];
      (pt.tags || []).forEach((t) => map[key].push(t));
    });
    return map;
  }, [productTags]);

  // Index LLM results by quote key: timestamp|text
  const llmResultsByKey = useMemo(() => {
    const map = {};
    llmResults.forEach((res) => {
      const key = `${res.quote_timestamp}|${res.quote}`;
      if (!map[key]) map[key] = [];
      map[key].push(res);
    });
    return map;
  }, [llmResults]);

  // Fetch challenge questions detail (Scope 1.3)
  const questionIds = useMemo(() => {
    const ids = new Set();
    const phaseList = getPhases(transcriptData);
    phaseList.forEach((phase) => {
      const qid = phase.question?.question_id;
      if (qid) ids.add(qid);
    });
    return Array.from(ids);
  }, [transcriptData]);

  const requestedQuestionIdsRef = useRef(new Set());
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    questionIds.forEach(async (qid) => {
      if (requestedQuestionIdsRef.current.has(qid)) return;
      requestedQuestionIdsRef.current.add(qid);
      try {
        const challengeData = await fetchChallengeQuestion(qid);
        if (isMountedRef.current && challengeData) {
          setQuestionsById((prev) => ({ ...prev, [qid]: challengeData }));
        }
      } catch (e) {
        // Allow retry on subsequent renders if request failed
        requestedQuestionIdsRef.current.delete(qid);
      }
    });
  }, [questionIds]);

  // Color map for multi-select LLM runs (Scope 1.5)
  const runColorMap = useMemo(() => {
    const map = {};
    const runIds = selectedLlmSources.filter((s) => s !== "product");
    runIds.forEach((rid, index) => {
      map[rid] = index % 6;
    });
    return map;
  }, [selectedLlmSources]);

  // Multi-select options for LLM Tags (Scope 1.5)
  const llmOptions = useMemo(() => {
    const opts = [{ value: "product", label: "Product Tags" }];
    sessionRuns.forEach((run) => {
      opts.push({
        value: run.id,
        label: run.display_name || `${run.prompt_name} — ${formatRunDate(run.finished_at || run.created_at)}`,
      });
    });
    return opts;
  }, [sessionRuns]);

  const currentBatch = batches.find((b) => b.id === selectedBatchId);
  const phases = getPhases(transcriptData);

  const savedSkillReviews =
    selection?.kind === "quote"
      ? Object.entries(smeReviews[selection.key] || {}).filter(
          ([, r]) => r.submitted && r.tags.size > 0
        )
      : [];
  const addableSkill = (vocabulary?.skills || []).find(
    (skill) => !savedSkillReviews.some(([saved]) => saved === skill)
  );

  return (
    <div className="sp-review-page">
      <div className="sp-page-header">
        <h1 className="sp-page-title" style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span>{currentBatch?.name || "SME Review"}</span>
          {currentSessionId && (
            <>
              <span style={{ color: "#6e7781", fontWeight: 400 }}>·</span>
              <span
                data-testid="header-session-id"
                style={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  fontSize: "0.85em",
                  fontWeight: 500,
                }}
              >
                {currentSessionId}
              </span>
              <CopyIconButton value={currentSessionId} title="Copy session ID" />
            </>
          )}
        </h1>
        <p className="sp-page-sub">
          Read the transcript, then tag skills on any quote. Click a quote to open
          a new tag. Click an LLM tag chip to agree, disagree, or add a confidence rating.
        </p>
      </div>

      {pageError && (
        <div className="sp-review-card sp-error-state" style={{ marginBottom: "16px", padding: "12px 16px" }}>
          {pageError}
          <button
            className="sp-link-btn"
            style={{ marginLeft: "12px" }}
            onClick={() => setPageError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Review Filters & Queue Navigation */}
      <div className="sp-review-filters">
        <div className="sp-review-field" style={{ flex: "1 1 240px", minWidth: "200px" }}>
          <label htmlFor="batch-select" className="sp-review-field-label">
            Batch
          </label>
          <select
            id="batch-select"
            className="sp-review-control"
            value={selectedBatchId}
            onChange={(e) => setSelectedBatchId(e.target.value)}
            disabled={batchesLoading || batches.length === 0}
          >
            {batches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.session_count ?? 0} sessions)
              </option>
            ))}
            {batches.length === 0 && <option value="">No batches available</option>}
          </select>
        </div>

        <div className="sp-review-field" style={{ flex: "1 1 320px", minWidth: "260px" }}>
          <label htmlFor="llm-tags-select" className="sp-review-field-label">
            LLM Tags
          </label>
          <Select
            inputId="llm-tags-select"
            classNamePrefix="sp-llm-select"
            isMulti
            isClearable
            placeholder="No Tags"
            isDisabled={sessionLoading || !currentSessionId}
            options={llmOptions}
            value={llmOptions.filter((opt) => selectedLlmSources.includes(opt.value))}
            onChange={(selected) => {
              const values = selected ? selected.map((s) => s.value) : [];
              setSelectedLlmSources(values);
            }}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: "36px",
                borderColor: "#d0d7de",
                fontSize: "0.9em",
              }),
            }}
          />
        </div>

        {/* Queue Navigation Prev/Next */}
        {queue && queue.items?.length > 0 && (
          <div className="sp-queue-nav">
            <span className="sp-queue-nav-info">
              Session {currentQueueIndex + 1} of {queue.items.length}
              {currentQueueItem?.reviewed ? (
                <span style={{ color: "#007A3F", marginLeft: "6px" }}>
                  <FontAwesomeIcon icon={faCheckCircle} /> Reviewed
                </span>
              ) : null}
            </span>
            <button
              type="button"
              className="sp-review-btn sp-review-btn-ghost sp-review-btn-sm"
              disabled={currentQueueIndex <= 0 || queueLoading}
              onClick={() => setCurrentQueueIndex((idx) => Math.max(0, idx - 1))}
              aria-label="Previous session"
            >
              <FontAwesomeIcon icon={faChevronLeft} /> Prev
            </button>
            <button
              type="button"
              className="sp-review-btn sp-review-btn-ghost sp-review-btn-sm"
              disabled={currentQueueIndex >= queue.items.length - 1 || queueLoading}
              onClick={() => setCurrentQueueIndex((idx) => Math.min(queue.items.length - 1, idx + 1))}
              aria-label="Next session"
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      {/* Main Two-Pane Layout */}
      <div className="sp-review-layout">
        {/* Left: Transcript Pane */}
        <div className="sp-transcript-pane" id="transcript-pane">
          {sessionLoading || queueLoading ? (
            <div className="sp-loading-state">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <div style={{ marginTop: "12px" }}>Loading session transcript…</div>
            </div>
          ) : !currentSessionId ? (
            <div className="sp-loading-state">Select a batch to begin review.</div>
          ) : phases.length === 0 ? (
            <div className="sp-loading-state">No transcript phases recorded for this session.</div>
          ) : (
            phases.map((phase, pIdx) => {
              const questionId = phase.question?.question_id;
              const challenge = questionId ? questionsById[questionId] : null;
              const questionText = phase.question?.text || (typeof phase.question === "string" ? phase.question : null);
              const durationText = formatDuration(phase.duration_seconds);

              return (
                <div key={phase.id || pIdx} className="sp-round">
                  <div className="sp-round-title">
                    {phase.label || `Round ${pIdx + 1}`}
                    {durationText ? ` · ${durationText}` : ""}
                  </div>

                  {challenge ? (
                    <div className="sp-round-question" data-testid={`round-question-${phase.id || pIdx}`}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                          flexWrap: "wrap",
                          gap: "8px",
                        }}
                      >
                        <div className="sp-q-label">Question</div>
                        {challenge.standards && challenge.standards.length > 0 && (
                          <StandardsBadges standards={challenge.standards} />
                        )}
                      </div>
                      <div
                        className="evaluator-mc-preview"
                        style={{
                          width: "100%",
                          border: "1px solid #eee",
                          borderRadius: "8px",
                          padding: "12px",
                          background: "#fff",
                        }}
                      >
                        <ConsensusActivityRender
                          hideQuestion={false}
                          challenge={buildPreviewChallenge(challenge)}
                          onMCAnswer={() => {}}
                          onORAnswer={() => {}}
                          isMathJaxSet={false}
                          showPromptFormula
                        />
                        {challenge.solution != null &&
                          (!challenge.answer_options || challenge.answer_options.length === 0) && (
                            <div
                              className="sp-expected-answer"
                              data-testid={`expected-answer-${questionId}`}
                              style={{
                                marginTop: "12px",
                                padding: "8px 12px",
                                background: "#f3fbf6",
                                border: "1px solid #007A3F",
                                borderRadius: "6px",
                                color: "#007A3F",
                                fontSize: "0.9em",
                              }}
                            >
                              <strong>Expected answer:</strong> {challenge.solution}
                            </div>
                          )}
                      </div>
                    </div>
                  ) : questionText ? (
                    <div className="sp-round-question">
                      <div className="sp-q-label">Question</div>
                      <div className="sp-q-text">{questionText}</div>
                    </div>
                  ) : null}

                  {(phase.events || []).map((evt, eIdx) => {
                    const cleanText = cleanEventContent(evt.content);
                    const quoteKey = `${evt.timestamp}|${cleanText}`;
                    const quotable = isQuotableSpeaker(evt);
                    const isSelectedQuote = selection?.kind === "quote" && selection.key === quoteKey;
                    const isSelectedLlm = selection?.kind === "llm" && selection.result?.quote_timestamp === evt.timestamp && selection.result?.quote === cleanText;

                    const utteranceClasses = ["sp-utterance", "utterance"];
                    if (quotable) utteranceClasses.push("quotable");
                    if (isSelectedQuote || isSelectedLlm) utteranceClasses.push("selected");

                    const matchingLlmResults = llmResultsByKey[quoteKey] || [];
                    const matchingProductTags = productTagsByKey[quoteKey] || [];
                    const smeSubmitted = Object.values(smeReviews[quoteKey] || {}).filter((r) => r.submitted && r.tags.size > 0);

                    return (
                      <div
                        key={eIdx}
                        className={utteranceClasses.join(" ")}
                        data-id={quoteKey}
                        onClick={() => {
                          if (quotable) handleSelectQuote(evt);
                        }}
                      >
                        <div className="sp-ts">{evt.timestamp}</div>
                        <div className="sp-body">
                          <span className={`sp-speaker ${getSpeakerClass(evt.speaker).replace("spk-", "sp-spk-")}`}>
                            {evt.speaker || parseType(evt.type)}
                          </span>
                          <span className="sp-text">{cleanText}</span>

                          {/* Chips Row: LLM chips, Product Tag chips, SME chips */}
                          {(matchingLlmResults.length > 0 || matchingProductTags.length > 0 || smeSubmitted.length > 0) && (
                            <div className="sp-tags-row">
                              {/* LLM Tag Chips */}
                              {matchingLlmResults.map((res) => {
                                const isReviewed = llmReviews[res.id]?.submitted || res.existing_review?.agreement;
                                const label = res.tags?.length ? res.tags.join(", ") : res.skill || "LLM Tag";
                                const colorIdx = runColorMap[res.run_id] ?? 0;
                                return (
                                  <span
                                    key={res.id}
                                    className={`sp-chip sp-chip-llm sp-chip-llm-${colorIdx} chip-llm clickable`}
                                    title="Click to review this LLM tag"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectLlmResult(res, evt);
                                    }}
                                  >
                                    {label}
                                    {isReviewed ? " ✓" : ""}
                                  </span>
                                );
                              })}

                              {/* Product Tag Chips */}
                              {matchingProductTags.map((pTag, ptIdx) => (
                                <span key={ptIdx} className="sp-chip sp-chip-product chip-product" title="Product tag — read-only">
                                  🔒 {pTag}
                                </span>
                              ))}

                              {/* SME Submitted Chips */}
                              {smeSubmitted.flatMap((r) =>
                                Array.from(r.tags).map((tag) => (
                                  <span key={`${r.skill}|${tag}`} className="sp-chip sp-chip-sme chip-sme">
                                    {tag}
                                  </span>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Right: Annotation Pane */}
        <div className="sp-annotation-pane" id="annotation-pane">
          {!selection ? (
            <div className="sp-annotation-empty">
              Select a quote in the transcript to tag a skill, or click an LLM tag to review it.
            </div>
          ) : selection.kind === "quote" ? (
            /* Quote Annotation Box */
            <div className="sp-review-card sp-annotation-card">
              <div className="sp-review-card-title">New tag</div>

              <div className="sp-quote-anchor">
                <div className="sp-quote-text">"{selection.event.content}"</div>
                <div className="sp-quote-meta">
                  {selection.event.speaker || parseType(selection.event.type)} · {selection.event.timestamp}
                </div>
              </div>

              {quoteSubmitted ? (
                /* Submitted View */
                <>
                  <div className="sp-submitted-banner">Submitted</div>
                  {savedSkillReviews.map(([skill, review]) => (
                    <div className="sp-skill-box" key={skill} data-testid={`skill-box-${skill}`}>
                      <div className="sp-review-field-label">{skill} Tags</div>
                      <div className="sp-tag-list">
                        {Array.from(review.tags).map((tag) => (
                          <div key={tag}>
                            <span className="sp-chip sp-chip-sme chip-sme">{tag}</span>
                            {review.reasons?.[tag] && (
                              <div className="sp-review-hint" style={{ marginTop: "4px" }}>
                                {review.reasons[tag]}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="sp-panel-actions">
                        <button
                          type="button"
                          className="sp-link-btn"
                          onClick={() => handleEditSkillTags(skill)}
                        >
                          Edit this tag
                        </button>
                        <button
                          type="button"
                          className="sp-link-btn"
                          disabled={quoteSaving}
                          onClick={() => handleClearSkillTags(skill)}
                        >
                          Clear Tags
                        </button>
                      </div>
                    </div>
                  ))}
                  {addableSkill && (
                    <button
                      type="button"
                      className="sp-review-btn sp-review-btn-ghost sp-review-btn-sm"
                      id="add-skill"
                      onClick={() => handleAddSkill()}
                    >
                      Add skill
                    </button>
                  )}
                </>
              ) : (
                /* Editing View */
                <>
                  {!vocabulary?.tags || vocabulary.tags.length === 0 ? (
                    <div
                      className="sp-alert sp-alert-warning"
                      role="alert"
                      data-testid="no-vocab-banner"
                      style={{ margin: "12px 0", display: "flex", alignItems: "flex-start", gap: "8px" }}
                    >
                      <FontAwesomeIcon icon={faExclamationCircle} style={{ marginTop: "3px" }} />
                      <div>
                        <strong>No tag vocabulary loaded.</strong> Run the tag loader to populate skills and tags.
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="sp-review-field">
                        <label htmlFor="skill-select" className="sp-review-field-label">
                          Skill
                        </label>
                        <select
                          id="skill-select"
                          className="sp-review-control"
                          value={activeSkill}
                          onChange={(e) => handleChangeSkill(e.target.value)}
                          disabled={vocabulary.skills.length <= 1}
                        >
                          {vocabulary.skills.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="sp-review-field">
                        <span className="sp-review-field-label">Tags</span>
                        <div className="sp-tag-list" id="tag-checklist">
                          {activeSkillTags.map((item) => {
                            const tag = item.tag;
                            const isChecked = activeQuoteTags.has(tag);
                            return (
                              <div key={tag}>
                                <label className={`sp-chip-toggle ${isChecked ? "checked" : ""}`}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const nextTags = new Set(activeQuoteTags);
                                      const nextReasons = { ...activeQuoteReasons };
                                      if (e.target.checked) {
                                        nextTags.add(tag);
                                      } else {
                                        nextTags.delete(tag);
                                        delete nextReasons[tag];
                                      }
                                      setActiveQuoteTags(nextTags);
                                      setActiveQuoteReasons(nextReasons);
                                    }}
                                  />
                                  {tag}
                                </label>
                                {isChecked && (
                                  <div className="sp-tag-reason">
                                    <textarea
                                      className="sp-review-control"
                                      rows="2"
                                      placeholder={`Why does this quote show ${tag}?`}
                                      value={activeQuoteReasons[tag] || ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setActiveQuoteReasons((prev) => ({
                                          ...prev,
                                          [tag]: val,
                                        }));
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {activeSkillTags.length === 0 && (
                            <div className="sp-review-hint">No tags available for {activeSkill}.</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="sp-panel-actions">
                    <button
                      type="button"
                      className="sp-review-btn sp-review-btn-primary sp-review-btn-sm"
                      id="submit-quote-tag"
                      disabled={activeQuoteTags.size === 0 || quoteSaving || !vocabulary?.tags?.length}
                      onClick={handleSubmitQuoteReview}
                    >
                      {quoteSaving ? <FontAwesomeIcon icon={faSpinner} spin /> : "Submit"}
                    </button>
                    <button
                      type="button"
                      className="sp-review-btn sp-review-btn-ghost sp-review-btn-sm"
                      id="clear-quote-tag"
                      disabled={quoteSaving}
                      onClick={handleClearQuoteReview}
                    >
                      Clear
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            /* LLM Review Box */
            <div className="sp-review-card sp-annotation-card">
              <div className="sp-review-card-title">
                LLM tag · {selection.result.tags?.join(", ") || selection.result.skill || "Tag"}
              </div>

              <div className="sp-quote-anchor">
                <div className="sp-quote-text">"{selection.event.content}"</div>
                <div className="sp-quote-meta">
                  {selection.event.speaker || parseType(selection.event.type)} · {selection.event.timestamp}
                </div>
              </div>

              {llmSubmitted ? (
                /* Submitted View */
                <>
                  <div className="sp-submitted-banner">Submitted</div>
                  <div className="sp-review-hint">
                    <strong>Agreement:</strong> {activeLlmAgree === "agree" ? "Agree" : "Disagree"}
                  </div>
                  {activeLlmConfidence && (
                    <div className="sp-review-hint">
                      <strong>Confidence:</strong>{" "}
                      {activeLlmConfidence === "H"
                        ? "High"
                        : activeLlmConfidence === "M"
                        ? "Medium"
                        : activeLlmConfidence === "L"
                        ? "Low"
                        : activeLlmConfidence}
                    </div>
                  )}
                  {activeLlmReason && (
                    <div className="sp-review-hint">
                      <strong>Reason:</strong> {activeLlmReason}
                    </div>
                  )}
                  <button
                    type="button"
                    className="sp-link-btn"
                    id="edit-llm-tag"
                    onClick={() => setLlmSubmitted(false)}
                  >
                    Edit this review
                  </button>
                </>
              ) : (
                /* Editing View */
                <>
                  {selection.result.rationale && (
                    <div className="sp-review-field">
                      <span className="sp-review-field-label">LLM rationale</span>
                      <div
                        className="sp-review-hint"
                        style={{
                          background: "#fafbfc",
                          border: "1px solid #EEEEEE",
                          borderRadius: "8px",
                          padding: "10px 12px",
                        }}
                      >
                        {selection.result.rationale}
                      </div>
                    </div>
                  )}

                  <div className="sp-review-field">
                    <span className="sp-review-field-label">Agree with this tag?</span>
                    <div className="sp-segmented">
                      <button
                        type="button"
                        className={`on-agree ${activeLlmAgree === "agree" ? "selected" : ""}`}
                        onClick={() => setActiveLlmAgree((cur) => (cur === "agree" ? null : "agree"))}
                      >
                        Agree
                      </button>
                      <button
                        type="button"
                        className={`on-disagree ${activeLlmAgree === "disagree" ? "selected" : ""}`}
                        onClick={() => setActiveLlmAgree((cur) => (cur === "disagree" ? null : "disagree"))}
                      >
                        Disagree
                      </button>
                    </div>
                  </div>

                  <div className="sp-review-field">
                    <span className="sp-review-field-label">Confidence</span>
                    <div className="sp-segmented">
                      <button
                        type="button"
                        className={`on-conf ${activeLlmConfidence === "H" ? "selected" : ""}`}
                        onClick={() => setActiveLlmConfidence((cur) => (cur === "H" ? null : "H"))}
                      >
                        High
                      </button>
                      <button
                        type="button"
                        className={`on-conf ${activeLlmConfidence === "M" ? "selected" : ""}`}
                        onClick={() => setActiveLlmConfidence((cur) => (cur === "M" ? null : "M"))}
                      >
                        Medium
                      </button>
                      <button
                        type="button"
                        className={`on-conf ${activeLlmConfidence === "L" ? "selected" : ""}`}
                        onClick={() => setActiveLlmConfidence((cur) => (cur === "L" ? null : "L"))}
                      >
                        Low
                      </button>
                    </div>
                  </div>

                  <div className="sp-review-field">
                    <label htmlFor="llm-reason" className="sp-review-field-label">
                      Reason
                    </label>
                    <textarea
                      id="llm-reason"
                      className="sp-review-control"
                      rows="3"
                      placeholder="Optional — add context for this call."
                      value={activeLlmReason}
                      onChange={(e) => setActiveLlmReason(e.target.value)}
                    />
                  </div>

                  <div className="sp-panel-actions">
                    <button
                      type="button"
                      className="sp-review-btn sp-review-btn-primary sp-review-btn-sm"
                      id="submit-llm-tag"
                      disabled={!activeLlmAgree || llmSaving}
                      onClick={handleSubmitLlmReview}
                    >
                      {llmSaving ? <FontAwesomeIcon icon={faSpinner} spin /> : "Submit"}
                    </button>
                    <button
                      type="button"
                      className="sp-review-btn sp-review-btn-ghost sp-review-btn-sm"
                      id="clear-llm-tag"
                      disabled={llmSaving}
                      onClick={handleClearLlmReview}
                    >
                      Clear
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
